import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import WaveLoader from '../components/WaveLoader';

// Plotly backgrounds matching the app design system
const BG = {
  plot_bgcolor: 'var(--bg-dark)',
  paper_bgcolor: 'var(--card-bg)',
  font: { family: 'inherit', size: 11 },
};

const plotCfg = {
  responsive: true,
  displaylogo: false,
  modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'],
};

// Semantic colors for answer types (data meaning, not UI chrome)
const TYPE_META = {
  yes_no:       { label: 'Yes / No',       color: '#1f7a3f' },
  rating_1to5:  { label: 'Rating 1–5',     color: '#1f5eff' },
  multi_choice: { label: 'Multi Choice',   color: '#c98b12' },
  open_text:    { label: 'Open Text',      color: '#7c3aed' },
};

// ---------------------------------------------------------------------------
// Micro-components
// ---------------------------------------------------------------------------

function NoData({ text = 'No data' }) {
  return (
    <div style={{ color: 'var(--text-lighter)', padding: '24px 0', textAlign: 'center', fontSize: 13 }}>
      {text}
    </div>
  );
}

function ResponseRate({ n, total = 87 }) {
  if (!n) return null;
  const pct = Math.round(100 * n / total);
  return (
    <span style={{ fontSize: 11, color: 'var(--text-lighter)', marginLeft: 8 }}>
      {n}/{total} answered ({pct}%)
    </span>
  );
}

function AnswerTypeBadge({ type }) {
  const meta = TYPE_META[type] || { label: type, color: '#888' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', padding: '2px 8px',
      borderRadius: 10, color: '#fff',
      background: meta.color, flexShrink: 0,

    }}>
      {meta.label}
    </span>
  );
}

// Outline button for drill-down triggers
function DrillButton({ color, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 6, padding: '5px 14px',
        background: 'var(--card-bg)',
        border: `1px solid ${color || 'var(--card-border)'}`,
        borderRadius: 8,
        color: color || 'var(--text-dark)',
        fontSize: 12, fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-block',
  
        transition: 'background 0.15s',
      }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Chart components
// ---------------------------------------------------------------------------

function YesNoChart({ data, onDrilldown }) {
  if (!data || (data.yes === 0 && data.no === 0)) return <NoData />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
      <Plot
        data={[{
          labels: [`Yes (${data.pct_yes}%)`, `No (${data.pct_no}%)`],
          values: [data.yes, data.no],
          type: 'pie', hole: 0.45,
          marker: { colors: ['#1f7a3f', '#c2410c'] },
          textinfo: 'percent', textposition: 'inside',
          hovertemplate: '%{label}<extra></extra>',
        }]}
        layout={{
          ...BG,
          height: 260,
          margin: { t: 16, b: 60, l: 16, r: 16 },
          showlegend: true,
          legend: { orientation: 'h', x: 0.5, xanchor: 'center', y: -0.12 },
        }}
        config={plotCfg}
        style={{ width: 300, flexShrink: 0 }}
        className="explorer-chart"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <DrillButton color="#1f7a3f" onClick={() => onDrilldown('true')}>
          Read Yes answers ({data.yes})
        </DrillButton>
        <DrillButton color="#c2410c" onClick={() => onDrilldown('false')}>
          Read No answers ({data.no})
        </DrillButton>
      </div>
    </div>
  );
}

function RatingChart({ data, onDrilldown }) {
  if (!data || !data.n) return <NoData />;
  const dist = data.distribution || {};
  const ratingMin = data.rating_min ?? 1;
  const ratingMax = data.rating_max ?? 5;
  const midpoint  = (ratingMin + ratingMax) / 2;
  const upper75   = ratingMin + 0.75 * (ratingMax - ratingMin);

  // Build bars high→low so highest rating is at the top
  const labels = [];
  const values = [];
  const clickRatings = [];
  for (let r = ratingMax; r >= ratingMin; r--) {
    labels.push(`${r} ★`);
    values.push(dist[String(r)] || 0);
    clickRatings.push(String(r));
  }
  const barColors = values.map((_, i) => {
    const rating = ratingMax - i;
    return rating >= upper75 ? '#1f7a3f' : rating >= midpoint ? '#c98b12' : '#c2410c';
  });
  const avgColor = data.avg >= upper75 ? '#1f7a3f' : data.avg >= midpoint ? '#c98b12' : '#c2410c';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 32, fontWeight: 900, color: avgColor }}>{data.avg}</span>
        <span style={{ fontSize: 13, color: 'var(--text-light)' }}>/ {ratingMax} · n={data.n}</span>
        <span style={{ fontSize: 11, color: 'var(--text-lighter)' }}>
          ({ratingMin}=Strongly Disagree, {ratingMax}=Strongly Agree)
        </span>
      </div>
      <Plot
        data={[{
          x: values, y: labels, type: 'bar', orientation: 'h',
          marker: { color: barColors },
          hovertemplate: '%{y}: %{x} responses<extra></extra>',
        }]}
        layout={{
          ...BG, height: Math.max(190, labels.length * 32 + 40),
          margin: { t: 8, b: 24, l: 48, r: 40 },
          xaxis: { title: 'Responses', dtick: 5 },
        }}
        config={plotCfg}
        style={{ width: '100%' }}
        onClick={(e) => {
          if (e.points && e.points[0]) onDrilldown(clickRatings[e.points[0].pointIndex]);
        }}
        className="explorer-chart clickable-plot"
      />
      <p style={{ fontSize: 11, color: 'var(--text-lighter)', marginTop: 4 }}>
        Click a bar to read what those respondents said.
      </p>
    </div>
  );
}

function MultiChoiceChart({ data, onDrilldown }) {
  if (!data || !data.options || !data.options.length) return <NoData />;
  const opts = [...data.options].reverse();
  return (
    <div>
      <Plot
        data={[{
          x: opts.map(o => o.count),
          y: opts.map(o => o.value.length > 48 ? o.value.slice(0, 46) + '…' : o.value),
          text: opts.map(o => `${o.pct}%`),
          textposition: 'outside',
          type: 'bar', orientation: 'h',
          marker: { color: '#1f5eff' },
          hovertemplate: '%{y}<br>%{x} interviews (%{text})<extra></extra>',
          customdata: opts.map(o => o.value),
        }]}
        layout={{ ...BG, height: Math.max(200, opts.length * 36 + 40), margin: { t: 8, b: 32, l: 240, r: 60 }, xaxis: { title: 'Interviews' } }}
        config={plotCfg}
        style={{ width: '100%' }}
        onClick={(e) => { if (e.points && e.points[0]) onDrilldown(e.points[0].customdata); }}
        className="explorer-chart clickable-plot"
      />
      <p style={{ fontSize: 11, color: 'var(--text-lighter)', marginTop: 4 }}>
        Click a bar to read what those interviewees said.
      </p>
    </div>
  );
}

function OpenTextCard({ data, onDrilldown }) {
  const n = data?.total_answered || 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0' }}>
      <span style={{ fontSize: 36, fontWeight: 900, color: '#1f5eff' }}>{n}</span>
      <div>
        <div style={{ fontSize: 13, color: 'var(--text-light)' }}>verbatim responses available</div>
        <DrillButton color="#1f5eff" onClick={() => onDrilldown(null)}>
          Read all {n} responses
        </DrillButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question card
// ---------------------------------------------------------------------------

function QuestionCard({ question, distribution, isLoading, onDrilldown }) {
  const n = (() => {
    if (!distribution) return null;
    if (distribution.total != null)         return distribution.total;
    if (distribution.n != null)             return distribution.n;
    if (distribution.total_answered != null) return distribution.total_answered;
    return null;
  })();

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1f5eff', flexShrink: 0 }}>
            {question.id}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {question.text}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <ResponseRate n={n} />
          <AnswerTypeBadge type={question.answer_type} />
        </div>
      </div>

      <div className="card-body">
        {isLoading && <div style={{ padding: '12px 0' }}><WaveLoader size="small" /></div>}

        {!isLoading && !distribution && <NoData text="Distribution not loaded" />}

        {!isLoading && distribution && (() => {
          switch (question.answer_type) {
            case 'yes_no':      return <YesNoChart data={distribution} onDrilldown={(v) => onDrilldown(question, v)} />;
            case 'rating_1to5': return <RatingChart data={distribution} onDrilldown={(v) => onDrilldown(question, v)} />;
            case 'multi_choice': return <MultiChoiceChart data={distribution} onDrilldown={(v) => onDrilldown(question, v)} />;
            case 'open_text':   return <OpenTextCard data={distribution} onDrilldown={(v) => onDrilldown(question, v)} />;
            default: return <NoData text={`Unknown answer type: ${question.answer_type}`} />;
          }
        })()}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section group — collapsible
// ---------------------------------------------------------------------------

function SectionGroup({ section, expanded, distributions, loadingQids, onToggle, onDrilldown }) {
  const answered = section.questions.filter(q => distributions[q.id]).length;
  const total    = section.questions.length;

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        type="button"
        onClick={() => onToggle(section)}
        style={{
          width: '100%', textAlign: 'left',
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 20px', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: 'var(--shadow-sm)',
          transition: 'all 0.2s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="badge info">
            {section.id}
          </span>
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-dark)', }}>
            {section.title}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
            {total} question{total !== 1 ? 's' : ''}
            {expanded && answered > 0 ? ` · ${answered} loaded` : ''}
          </span>
        </div>
        <span style={{ fontSize: 18, color: '#1f5eff', fontWeight: 700, lineHeight: 1 }}>
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div style={{ marginTop: 8, paddingLeft: 16, borderLeft: '3px solid var(--primary, #1f5eff)' }}>
          {section.questions.map(q => (
            <QuestionCard
              key={q.id}
              question={q}
              distribution={distributions[q.id] || null}
              isLoading={loadingQids.has(q.id)}
              onDrilldown={onDrilldown}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quote drill-down panel
// ---------------------------------------------------------------------------

function QuoteDrilldown({ open, question, filterValue, answers, loading, onClose }) {
  if (!open) return null;

  const filterLabel = (() => {
    if (!filterValue) return '';
    if (filterValue === 'true')  return ' — Yes answers';
    if (filterValue === 'false') return ' — No answers';
    if (['1','2','3','4','5'].includes(filterValue)) return ` — Rated ${filterValue}/5`;
    return ` — "${filterValue}"`;
  })();

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 999 }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0,
        width: 460, height: '100vh',
        background: 'var(--card-bg)',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        zIndex: 1000,
        display: 'flex', flexDirection: 'column',
        borderLeft: '3px solid #1f5eff',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: '#171717',
          color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div style={{ minWidth: 0, paddingRight: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, opacity: 0.7, marginBottom: 2 }}>
              {question?.id}{filterLabel}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>
              {question?.text}
            </div>
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
              {loading ? 'Loading…' : `${answers.length} response${answers.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
              fontSize: 20, cursor: 'pointer', borderRadius: 6,
              width: 32, height: 32, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Answers */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {loading && <div style={{ textAlign: 'center', padding: '40px 0' }}><WaveLoader /></div>}

          {!loading && answers.length === 0 && (
            <div style={{ color: 'var(--text-lighter)', padding: '40px 0', textAlign: 'center', fontSize: 13 }}>
              No verbatim responses found for this selection.
            </div>
          )}

          {!loading && answers.map((a, i) => {
            const mainText = a.verbatim_answer || a.evidence_from_transcript || a.rephrased_answer;
            const subText  = a.rephrased_answer && a.rephrased_answer !== mainText ? a.rephrased_answer : null;
            const meta = [a.state, a.ro, a.tenure, a.sentiment_label ? `sentiment: ${a.sentiment_label}` : null].filter(Boolean).join(' · ');

            return (
              <div key={i} style={{
                borderLeft: '3px solid #1f5eff',
                paddingLeft: 14, paddingTop: 10, paddingBottom: 10,
                marginBottom: 12,
                background: 'var(--bg-dark)',
                borderRadius: '0 8px 8px 0',
              }}>
                {mainText ? (
                  <p style={{ margin: 0, fontStyle: 'italic', color: 'var(--text-dark)', fontSize: 13, lineHeight: 1.6 }}>
                    "{mainText}"
                  </p>
                ) : (
                  <p style={{ margin: 0, color: 'var(--text-lighter)', fontSize: 12 }}>[No verbatim text]</p>
                )}
                {subText && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-light)' }}>{subText}</p>
                )}
                {meta && (
                  <p style={{ margin: '5px 0 0', fontSize: 11, color: 'var(--text-lighter)' }}>
                    — {meta}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function HrExplorer({ loading, setLoading }) {
  const [questionnaire, setQuestionnaire]       = useState(null);
  const [expandedSections, setExpandedSections] = useState({ S3: true });
  const [distributions, setDistributions]       = useState({});
  const [loadingQids, setLoadingQids]           = useState(new Set());

  const [drillOpen, setDrillOpen]         = useState(false);
  const [drillQuestion, setDrillQuestion] = useState(null);
  const [drillFilter, setDrillFilter]     = useState(null);
  const [drillAnswers, setDrillAnswers]   = useState([]);
  const [drillLoading, setDrillLoading]   = useState(false);

  const loadSectionDists = useCallback((questions) => {
    setDistributions(prev => {
      setLoadingQids(prevLoading => {
        const toLoad = questions.filter(q => !prev[q.id] && !prevLoading.has(q.id));
        if (!toLoad.length) return prevLoading;

        const next = new Set(prevLoading);
        toLoad.forEach(q => next.add(q.id));

        Promise.all(
          toLoad.map(q =>
            axios.get(`/api/hr/explorer/question-distribution?question_id=${q.id}`)
              .then(r => ({ id: q.id, data: r.data }))
              .catch(() => ({ id: q.id, data: null }))
          )
        ).then(results => {
          setDistributions(d => {
            const updated = { ...d };
            results.forEach(r => { if (r.data) updated[r.id] = r.data; });
            return updated;
          });
          setLoadingQids(l => {
            const cleaned = new Set(l);
            results.forEach(r => cleaned.delete(r.id));
            return cleaned;
          });
        });

        return next;
      });
      return prev;
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/hr/explorer/questionnaire')
      .then(res => {
        setQuestionnaire(res.data);
        const s3 = (res.data.sections || []).find(s => s.id === 'S3');
        if (s3) loadSectionDists(s3.questions);
      })
      .catch(err => console.error('HR Explorer load error:', err))
      .finally(() => setLoading(false));
  }, [setLoading, loadSectionDists]);

  const toggleSection = useCallback((section) => {
    const opening = !expandedSections[section.id];
    setExpandedSections(prev => ({ ...prev, [section.id]: opening }));
    if (opening) loadSectionDists(section.questions);
  }, [expandedSections, loadSectionDists]);

  const openDrilldown = useCallback((question, filterValue) => {
    setDrillQuestion(question);
    setDrillFilter(filterValue || null);
    setDrillOpen(true);
    setDrillAnswers([]);
    setDrillLoading(true);

    const url = `/api/hr/explorer/question-verbatim?question_id=${question.id}` +
      (filterValue != null ? `&filter_value=${encodeURIComponent(filterValue)}` : '');

    axios.get(url)
      .then(r => setDrillAnswers(r.data.answers || []))
      .catch(() => setDrillAnswers([]))
      .finally(() => setDrillLoading(false));
  }, []);

  return (
    <div>
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Structured Questionnaire
        </div>
        <h2>Interview Explorer</h2>
        <p>
          All structured questions from the exit interview questionnaire.
          Expand a section to see answer distributions. Click any bar or button to read verbatim responses.
        </p>
      </div>

      {!questionnaire && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <WaveLoader size="large" />
        </div>
      )}

      {questionnaire && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <div className="metric-card">
              <span className="metric-label">Total Questions</span>
              <div className="metric-value" style={{ color: '#1f5eff' }}>
                {questionnaire.sections.reduce((a, s) => a + s.questions.length, 0)}
              </div>
            </div>
            <div className="metric-card">
              <span className="metric-label">Sections</span>
              <div className="metric-value" style={{ color: '#1f5eff' }}>{questionnaire.sections.length}</div>
            </div>
            <div className="metric-card">
              <span className="metric-label">Exit Interviews</span>
              <div className="metric-value" style={{ color: '#1f7a3f' }}>87</div>
            </div>
            <div className="metric-card">
              <span className="metric-label">Questionnaire</span>
              <div className="metric-value secondary" style={{ fontSize: 18 }}>{questionnaire.questionnaire_key}</div>
            </div>
          </div>

          {questionnaire.sections.map(section => (
            <SectionGroup
              key={section.id}
              section={section}
              expanded={!!expandedSections[section.id]}
              distributions={distributions}
              loadingQids={loadingQids}
              onToggle={toggleSection}
              onDrilldown={openDrilldown}
            />
          ))}
        </div>
      )}

      <QuoteDrilldown
        open={drillOpen}
        question={drillQuestion}
        filterValue={drillFilter}
        answers={drillAnswers}
        loading={drillLoading}
        onClose={() => setDrillOpen(false)}
      />
    </div>
  );
}

export default HrExplorer;
